import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import paper from "paper";
import {nanoid} from 'nanoid';
import {Color, MusicPaperShape, ShapeSchemeType, Track} from "@consts/types";
import useCopyPast from "@hooks/useCopyPast";
import {AbstractFigure, Blob, Ellipse, Polygon, Rectangle} from "@common/new-drawer/entity";
import Sidebar from "@common/new-drawer/parts/sidebar";
import ControlGroup from "@common/new-drawer/parts/control-group";
import {createComplexMusicShape, createSimpleMusicShape} from "@common/new-drawer/utils/shape-creators";
import isSimpleShapeSchemeType from "../utils/guards/isSimpleShapeSchemeType";
import createBackgroundRectangle from "../utils/create-background-rectangle";
import {Container} from "../styleds/root";
import {TitleTextFields} from './title-text-field'
import Canvas from './canvas'
import {Input} from "@material-ui/core";
import useDeleteKey from "@hooks/useDeleteKey";

type ShapeMap = {
  [key: string]: AbstractFigure;
}

type CanvasProps = {
  title: string;
  shapeList: MusicPaperShape[];
  backgroundColor: Color;
  isPlayMode: boolean;
  updateShapeList: (type: MusicPaperShape[]) => void;
  updateBackgroundColor: (color: Color) => void;
  updateTitle: (title: string) => void;
  openUploadModal: VoidFunction;
  openDownloadModal: VoidFunction;
  setPlayMode: (status: boolean) => void;
}

const createShape = (scope: paper.PaperScope, shapeData: MusicPaperShape) => {
  switch (shapeData.type) {
    case ShapeSchemeType.rectangle:
      return new Rectangle(scope, shapeData);
    case ShapeSchemeType.ellipse:
      return new Ellipse(scope, shapeData);
    case ShapeSchemeType.polygon:
      return new Polygon(scope, shapeData);
    case ShapeSchemeType.blob:
      return new Blob(scope, shapeData);
  }
}

const createShapeMap = (shapeList: AbstractFigure[]) => {
  return shapeList.reduce<ShapeMap>((map, shape) => ({
    ...map,
    [shape.id]: shape
  }), {})
}

const Drawer: FC<CanvasProps> = ({
  title,
  shapeList,
  backgroundColor,
  isPlayMode,
  updateShapeList,
  updateBackgroundColor,
  updateTitle,
  openUploadModal,
  openDownloadModal,
  setPlayMode
}) => {
  const [selectShapeId, setSelectShapeId] = useState<string | null>(null);
  const pastBufferRef = useRef<MusicPaperShape | null>(null);
  const shapeListRef = useRef<MusicPaperShape[]>([]);
  shapeListRef.current = shapeList;
  const selectShape = useMemo<MusicPaperShape | null>(() => {
    const value = shapeList.find(shape => shape.id === selectShapeId)
    return value || null;
  }, [selectShapeId, shapeList])


  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const shapeArrRef = useRef<AbstractFigure[]>([]);
  const backgroundRef = useRef<Rectangle | null>(null);
  const scopeRef = useRef<paper.PaperScope | null>();

  const titleInputRef = useRef<HTMLInputElement>(null);

  const updateShapeItem = useCallback((updateShape: MusicPaperShape) => {
    const actualShapeList = shapeListRef.current
    const selectShapeIndex = actualShapeList.findIndex((shape) => shape.id === updateShape.id);
    if (selectShapeIndex === -1) return;
    updateShapeList([
      ...actualShapeList.slice(0, selectShapeIndex),
      updateShape,
      ...actualShapeList.slice(selectShapeIndex + 1)
    ])

  }, [updateShapeList])

  const handleResizeWindow = useCallback(() => {
    const container = containerRef.current;
    const background = backgroundRef.current;
    const scope = scopeRef.current;

    if (!container || !background || !scope) return;
    const { clientWidth: width, clientHeight: height } = container;

    scope.view.viewSize.width = width;
    scope.view.viewSize.height = height;

    background.updateFromShapeData({
      ...background.shapeData,
      shapeScheme: {
        ...background.shapeData.shapeScheme,
        size: { width, height },
      }
    });
    scope.view.update();
  }, [])

  useEffect(() => {
    if (!canvasRef.current) throw new Error('Expected canvas element missing');
    const scope = new paper.PaperScope()
    scope.setup(canvasRef.current);
    scopeRef.current = scope;

    const background = createBackgroundRectangle(scope);
    background.isDisable = true;
    backgroundRef.current = background;
    const selectShape: { current: MusicPaperShape | null } = {
      current: null,
    };

    const handleShapeUpdate = (shapeData: MusicPaperShape) => {
      const actualShapeList = shapeListRef.current
      const selectShapeIndex = actualShapeList.findIndex((shape) => shape.id === shapeData.id);
      if (selectShapeIndex === -1) return;
      updateShapeList([
        ...actualShapeList.slice(0, selectShapeIndex),
        shapeData,
        ...actualShapeList.slice(selectShapeIndex + 1)
      ])
    }

    const handleShapeFocused = (shapeData: MusicPaperShape) => {
      if (shapeData.id === backgroundRef.current?.id) {
        setSelectShapeId(null);
        return;
      }

      const actualShapeList = shapeListRef.current
      const selectShapeIndex = actualShapeList.findIndex((shape) => shape.id === shapeData.id);
      if (selectShapeIndex === -1) return;
      updateShapeList([
        ...actualShapeList.slice(0, selectShapeIndex),
        ...actualShapeList.slice(selectShapeIndex + 1),
        shapeData,
      ])

      if (selectShape.current?.id !== shapeData.id) {
        setSelectShapeId(shapeData.id);
      }
    }

    const handleCanvasClick = () => {
      titleInputRef.current?.focus();
    }

    const containerElement = containerRef.current;
    window.addEventListener('resize', handleResizeWindow);
    containerElement?.addEventListener('click', handleCanvasClick);

    AbstractFigure.emitter.on('click', handleShapeFocused);
    AbstractFigure.emitter.on('edited', handleShapeUpdate);
    return () => {
      window.removeEventListener('resize', handleResizeWindow)
      containerElement?.removeEventListener('click', handleCanvasClick);

      AbstractFigure.emitter.off('click', handleShapeFocused)
      AbstractFigure.emitter.on('edited', handleShapeUpdate)
    }
  }, [setSelectShapeId, updateShapeList, handleResizeWindow])

  useEffect(() => {
    if (!scopeRef.current) throw new Error('Expected scope missing');
    const shapeMap = createShapeMap(shapeArrRef.current)
    const scope = scopeRef.current;
    shapeArrRef.current = [];
    shapeList.forEach(shapeData => {
      let shape = shapeMap[shapeData.id];
      if (shape) {
        shape.updateFromShapeData(shapeData);
        delete shapeMap[shapeData.id];
      } else {
        shape = createShape(scope, shapeData)
      }
      shapeArrRef.current.push(shape);
      shape.bringToFront();
    })

    // const reverseShapeArr = [...shapeArrRef.current].reverse();
    // reverseShapeArr.forEach(shape => shape.bringToFront());

    Object.values(shapeMap).forEach((shape) => {
      shape.remove();
    })
  }, [shapeList])

  useEffect(() => {
    if (!selectShape) return;
    const shape = shapeArrRef.current.find(shape => shape.id === selectShape.id);
    if (!shape) return;
    shapeArrRef.current.forEach(shape => shape.isActive = false);
    shape.isActive = true;
  }, [selectShape])

  useEffect(() => {
    const background = backgroundRef.current;
    if (!background) return;
    background.updateFromShapeData({
      ...background.shapeData,
      color: backgroundColor
    })
  }, [backgroundColor])

  useEffect(() => {
    shapeArrRef.current.forEach(shape => shape.isPlayMode = isPlayMode);
    handleResizeWindow();
    setSelectShapeId(null);
  }, [isPlayMode, setSelectShapeId, handleResizeWindow])


  const handleCopy = useCallback(() => {
    if (!selectShape) return;
    pastBufferRef.current = { ...selectShape };
  }, [selectShape]);

  const handlePast = useCallback(() => {
    if (!pastBufferRef.current) return;
    const bufferValueShape: MusicPaperShape = {
      ...pastBufferRef.current,
      id: nanoid()
    };

    setSelectShapeId(bufferValueShape.id);
    updateShapeList([
      ...shapeListRef.current,
      bufferValueShape
    ]);


  }, [updateShapeList, setSelectShapeId]);

  useCopyPast(handleCopy, handlePast);
  useDeleteKey(() => {
    if (!selectShape) return;
    removeMusicShape(selectShape);
  });


  const createMusicShape = useCallback((type: ShapeSchemeType) => {
    const musicShape = isSimpleShapeSchemeType(type) ? createSimpleMusicShape(type) : createComplexMusicShape(type);
    updateShapeList([
      ...shapeList,
      musicShape
    ])
  }, [shapeList, updateShapeList])

  const removeMusicShape = useCallback((shape: MusicPaperShape) => {
    const index = shapeList.findIndex(item => item.id === shape.id);
    if (index === -1) return;

    updateShapeList([
      ...shapeList.slice(0 ,index),
      ...shapeList.slice(index + 1),
    ])
  }, [shapeList, updateShapeList])

  const handleColorChange = useCallback((color: Color) => {
    if (selectShape) {
      updateShapeItem({  ...selectShape, color });
    } else {
      updateBackgroundColor(color)
    }
  }, [
    selectShape,
    updateBackgroundColor,
    updateShapeItem
  ])

  const handleTrackChange = useCallback((track: Track) => {
    if (!selectShape) return;
    updateShapeItem({  ...selectShape, track });
  }, [selectShape, updateShapeItem])


  return (
    <>
      <Container ref={containerRef}>
        <TitleTextFields>
          <Input
            value={title}
            ref={titleInputRef}
            placeholder='Title'
            fullWidth
            onChange={event => updateTitle(event.target.value)}
          />
        </TitleTextFields>
        <ControlGroup
          selectShape={selectShape}
          isPlayMode={isPlayMode}
          createFigure={createMusicShape}
          removeFigure={removeMusicShape}
          openDownloadModal={openDownloadModal}
          openUploadModal={openUploadModal}
          setPlayMode={setPlayMode}
        />
        <Canvas canvasRef={canvasRef} />
        {!isPlayMode && (
          <Sidebar
            selectShape={selectShape}
            background={backgroundColor}
            onColorChange={handleColorChange}
            onTrackChange={handleTrackChange}
          />
        )}
      </Container>
    </>
);
}

export default Drawer;
